import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { Avatar } from "@fidget/common/components/avatar/avatar";
import { DefaultErrorFallback, withErrorBoundary } from "@fidget/common/components/errorBoundary";
import { SimpleIndicator } from "@fidget/common/components/indicators/simpleIndicator/simpleIndicator";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { getRelativeDateString, Mode } from "@fidget/common/utils/timeFormatter";
import { InboxEntryTypeEnum, SnoozedStatus } from "../../models/base/Conversation";
import { InboxFilterId } from "../../models/inbox";
import { IInboxEntry } from "../../models/inbox/InboxEntry";
import { IInboxFilter, UnreadType } from "../../models/inbox/InboxFilter";
import { GuestStatusIndicator } from "../status/GuestStatusIndicator";
import styles from "./inboxEntry.css";
import { SnoozeIcon } from "./snoozeIcon/snoozeIcon";
import { UnsnoozedIcon } from "./unsnoozedIcon/unsnoozedIcon";
import { UserRole } from "@closerplatform/spinner-openapi";

interface Props {
  entry: IInboxEntry;
}
export enum MessageType {
  GUEST_MESSAGE,
  AGENT_MESSAGE,
  NOTE,
  TYPING
}

type IconName = "icon-files" | "icon-cal-icon" | "icon-call" | "icon-doc";

const InboxEntryComponent = observer(({ entry }: Props) => {
  const [fuzzyTimeUpdateTimer, setFuzzyTimeUpdateTimer] = useState<number>();
  const [guestStatusChanged, setGuestStatusChagned] = useState<boolean>();
  const [time, setTime] = useState<string>();

  useEffect(() => {
    const timer = window.setInterval(() => {
      setTime(getFuzzyTime());
    }, 30000);

    setFuzzyTimeUpdateTimer(timer);
    setTime(getFuzzyTime());

    return () => {
      if (fuzzyTimeUpdateTimer) {
        clearInterval(fuzzyTimeUpdateTimer);
      }
    };
  }, []);

  useEffect(() => {
    if (guestStatusChanged === undefined) {
      setGuestStatusChagned(false);
    } else {
      setGuestStatusChagned(true);
    }
  }, [entry.owner.status]);

  useEffect(() => {
    setTime(getFuzzyTime());
  }, [entry.timestamp]);

  const toggleSnooze = (event: React.MouseEvent) => {
    event.stopPropagation();
    entry.toggleSnooze();
  };

  const stopPropagationOnMouseEnter = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const getFuzzyTime = (): string => getRelativeDateString(entry.timestamp, { mode: Mode.SHORT });

  const renderIconWithText = (iconName: IconName, element: JSX.Element) => (
    <div className={classNames(styles["message-container"], styles["icon-container"])}>
      <i className={classNames(iconName, styles["content-icon"])} />
      {element}
    </div>
  );

  const renderContent = () => {
    const { sender } = entry;
    if (entry.lastItemType === InboxEntryTypeEnum.NOTE) {
      return (
        <div className={classNames(styles["message-container"], styles["note-message"])}>
          {entry.message}
        </div>
      );
    }
    if (entry.lastItemType === InboxEntryTypeEnum.FILES) {
      return renderIconWithText(
        "icon-files",
        <L10n
          id={
            sender && sender.role !== UserRole.LEAD
              ? "inbox.message.files.agent"
              : "inbox.message.files.customer"
          }
        />
      );
    }
    if (entry.lastItemType === InboxEntryTypeEnum.MEETING_SCHEDULED) {
      return renderIconWithText(
        "icon-cal-icon",
        <L10n id={"conversation.meeting.meetingScheduled"} />
      );
    }
    if (entry.lastItemType === InboxEntryTypeEnum.MEETING_RESCHEDULED) {
      return renderIconWithText(
        "icon-cal-icon",
        <L10n id={"conversation.meeting.meetingRescheduled"} />
      );
    }
    if (entry.lastItemType === InboxEntryTypeEnum.MEETING_CANCELLED) {
      return renderIconWithText(
        "icon-cal-icon",
        <L10n id={"conversation.meeting.meetingCancelled"} />
      );
    }
    if (entry.lastItemType === InboxEntryTypeEnum.CALL_STARTED) {
      return renderIconWithText("icon-call", <L10n id={"inbox.entryMessage.callStarted"} />);
    }
    if (entry.lastItemType === InboxEntryTypeEnum.CALL_ENDED) {
      return renderIconWithText("icon-call", <L10n id={"inbox.entryMessage.callEnded"} />);
    }
    if (entry.lastItemType === InboxEntryTypeEnum.FORM) {
      return renderIconWithText(
        "icon-doc",
        <L10n
          id={
            sender && sender.role !== UserRole.LEAD
              ? "inbox.entryMessage.form.adviser"
              : "inbox.entryMessage.form.customer"
          }
        />
      );
    }
    if (entry.usersTypingEvents.length > 0) {
      return <div className={classNames(styles["message-container"], styles["typing-message"])} />;
    }
    if (entry.message && entry.message.length > 0) {
      return (
        <div
          className={classNames(styles["message-container"], {
            [styles["agent-message"]]: sender && sender.role !== UserRole.LEAD
          })}
        >
          {entry.message}
        </div>
      );
    }

    return undefined;
  };

  const render = () => {
    const { assignee, owner, isSelected, isSnoozed } = entry;
    const inboxFilter = entry.filter as IInboxFilter;
    const shouldShowAvatar = inboxFilter.id !== InboxFilterId.YOURS;

    return (
      <div
        className={classNames(styles.message, {
          [styles.current]: isSelected,
          [styles.snoozed]: isSnoozed
        })}
      >
        <div className={styles.header}>
          <div className={classNames(styles.name, { [styles["random-name"]]: !owner.hasName })}>
            <GuestStatusIndicator shouldAnimate={guestStatusChanged} status={owner.status} />
            <span className={styles["name-text"]}>{owner.displayedName}</span>
          </div>
          {shouldShowAvatar && assignee && (
            <TooltipComponent
              positionPreset={"bottom"}
              text={assignee.displayedName}
              maxWidth={"140px"}
              breakWords
            >
              <div className={styles.agent}>
                <Avatar user={assignee} />
              </div>
            </TooltipComponent>
          )}
          <div className={styles.time}>{time}</div>
        </div>
        <div className={styles.content}>
          {renderContent()}
          {inboxFilter.unreadType === UnreadType.MESSAGES && !!entry.unreadCount && (
            <SimpleIndicator count={entry.unreadCount} />
          )}
        </div>
        {inboxFilter.id === InboxFilterId.YOURS && (
          <div className={styles.footer}>
            {entry.snoozedStatus === SnoozedStatus.Unsnoozed && <UnsnoozedIcon />}
          </div>
        )}
        {inboxFilter.id === InboxFilterId.YOURS && (
          <div className={styles["snooze-container"]}>
            <TooltipComponent
              text={intl.formatMessage(
                entry.isSnoozed ? "inbox.unsnoozeConversation" : "inbox.snoozeConversation"
              )}
              positionPreset={"top"}
              maxWidth={"160px"}
              changeTransformTooltip={"translateX(-80%)"}
              changeTransformOutset={"translateX(300%)"}
            >
              <SnoozeIcon
                onClick={toggleSnooze}
                onMouseDown={stopPropagationOnMouseEnter}
                hoverable
              />
            </TooltipComponent>
          </div>
        )}
      </div>
    );
  };

  return render();
});

export const InboxEntry = withErrorBoundary(InboxEntryComponent, () => (
  <div className={styles.message}>
    <DefaultErrorFallback />
  </div>
));
